import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ActionBar } from '~/components/ui/action-bar';
import { Button } from '~/components/ui/button';
import { artifactDetailPath, artifactKeys, type ArtifactDetail, type EditableArtifactKind } from '~/hooks/queries/use-artifacts';
import { api } from '~/lib/api-client';
import {
  deleteSlide,
  duplicateSlide,
  emptyDraft,
  isArtifactDraftDirty,
  moveSlide,
  parseDeckBody,
  buildSaveBody,
  reapplyEdits,
  reapplyMessage,
  recordChange,
  restorePayload,
  rowIndexOfOriginal,
  rowsOf,
  setSlideNotes,
  slideOfBlockId,
  slidesAfterSave,
  type ArtifactDraft,
  type OriginalSlide,
} from './artifact-edit-draft';
import {
  ARTIFACT_CONFLICT_SAVE_REASON,
  classifySaveRefusal,
  frameKeepsReloadingReason,
  shouldReportFrameUnstable,
  planFrameNavigation,
  editFrameSrc,
  planServerVersion,
} from './artifact-edit-logic';
import { useFrameVersion } from './artifact-updated';
import { createFrameLink } from './edit-frame-link';
import { canvasFrameAllowsFullscreen, canvasFrameSandbox } from './canvas-frame';
import { CanvasSlideStrip } from './canvas-slide-strip';
import { SAVING_REASON, type StripAction } from './canvas-slide-strip-gate';
import { clearCanvasDraft, loadArtifactDraft, saveArtifactDraft } from './canvas-storage';
import { createDebouncedWriter } from './debounced-writer';
import type { CanvasFlush, CanvasFlushResult } from './canvas-send-gate';
import type { CanvasSaveStatus } from './canvas-document';

/** Trailing delay for the draft's localStorage write; flushed on unmount, pagehide, save and switch. */
const DRAFT_WRITE_MS = 300;

interface Snapshot {
  updatedAt: string;
  body: string;
}

interface PendingReapply {
  draft: ArtifactDraft;
  /** The slides the draft was made against: only used to say whether slide changes are being dropped. */
  originals: readonly OriginalSlide[] | null;
}

export interface ArtifactEditorProps {
  sessionId: string;
  kind: EditableArtifactKind;
  slug: string;
  title: string;
  /** The live artifact detail (the panel's query): its `updatedAt` moving is one way Eddy's change is noticed. */
  detail: ArtifactDetail;
  onStatusChange: (status: CanvasSaveStatus) => void;
  onDirtyChange: (dirty: boolean) => void;
  /**
   * The framed page keeps reloading itself and the draft is CLEAN: the panel shows the plain page instead
   * (a locked editor would leave nothing usable). Not called when the draft is dirty — that keeps the
   * locked editor so the typed text is never lost.
   */
  onFrameUnstable?: () => void;
  /** Where the editor registers its save-before-send flush (canvas-send-gate.ts). */
  flushRef?: MutableRefObject<CanvasFlush | null>;
  /** Deck only: the person pressed "Arrange slides", so the slide strip (order, duplicate, delete, notes) is shown under the deck. */
  arrangeOpen?: boolean;
  /**
   * The item stopped being editable while this draft was unsaved (set by the panel). The editor stays
   * mounted with the frame locked and Save off, so the typed text is not silently lost; Discard works.
   */
  blockedReason?: string | null;
}

const blocksOf = (raw: unknown): Record<string, string> => {
  const out: Record<string, string> = {};
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    for (const [id, html] of Object.entries(raw as Record<string, unknown>)) if (typeof html === 'string') out[id] = html;
  }
  return out;
};

/**
 * FRONT-2598 — edit mode for a sketch or a deck: the served page in an iframe with
 * its text blocks editable, a draft of what changed, and the same save / discard /
 * conflict controls as the document editor. Wire format: src/artifacts/editor-script.ts.
 */
export function ArtifactEditor({ sessionId, kind, slug, title, detail, onStatusChange, onDirtyChange, onFrameUnstable, flushRef, arrangeOpen = false, blockedReason = null }: ArtifactEditorProps) {
  const queryClient = useQueryClient();
  const itemKey = `${kind}:${slug}`;
  const detailKey = artifactKeys.detail(kind, slug);

  const [snapshot, setSnapshot] = useState<Snapshot>({ updatedAt: detail.updatedAt, body: detail.body });
  const [loadId, setLoadId] = useState(0);
  const [initial] = useState(() => {
    const stored = loadArtifactDraft(sessionId, itemKey);
    const dirty = !!stored && isArtifactDraftDirty(stored, kind === 'deck' ? parseDeckBody(detail.body) : null);
    // A stored draft started against another version is a conflict, not something to apply silently.
    return { draft: dirty && stored ? stored : emptyDraft(detail.updatedAt), conflict: dirty && stored!.base !== detail.updatedAt };
  });
  const [draft, setDraft] = useState<ArtifactDraft>(initial.draft);
  const [conflict, setConflictState] = useState(initial.conflict);
  // Why the conflict bar is up when it is not "Eddy changed it": a save the server refused because some typed edit
  // matched no block. Cleared with the conflict.
  const [conflictNote, setConflictNote] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [selected, setSelected] = useState(0);

  const originals = useMemo(() => (kind === 'deck' ? parseDeckBody(snapshot.body) : null), [kind, snapshot.body]);

  // Latest values for code that outlives the render it started in (message listener, async save).
  const draftRef = useRef(draft);
  const snapshotRef = useRef(snapshot);
  const originalsRef = useRef(originals);
  originalsRef.current = originals;
  const conflictRef = useRef(conflict);
  const savingRef = useRef(false);
  const inventoryRef = useRef<Record<string, string> | null>(null);
  const pendingReapplyRef = useRef<PendingReapply | null>(null);
  const reloadingRef = useRef(false);
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const selectedRef = useRef(0);
  /** After a deck save the reloaded page scrolls back to the slide the person was on (set by the save, used by the next first `ready`). */
  const pendingGotoRef = useRef<number | null>(null);
  const blockedRef = useRef(blockedReason);
  blockedRef.current = blockedReason;
  const portHandlerRef = useRef<(data: unknown) => void>(() => {});
  // The private channel to the frame (edit-frame-link.ts): created once, reset whenever the iframe is replaced.
  // A second hello from the frame's own window means the framed page navigated itself (see onFrameNavigated).
  const navigatedRef = useRef<() => void>(() => {});
  const [link] = useState(() =>
    createFrameLink(
      (data) => portHandlerRef.current(data),
      () => navigatedRef.current(),
    ),
  );
  const frameNavsRef = useRef(0);
  const remountPendingRef = useRef(false);
  const [frameLocked, setFrameLocked] = useState(false);
  const frameLockedRef = useRef(false);
  const inFlightRef = useRef<Promise<CanvasFlushResult> | null>(null);
  const [writer] = useState(() =>
    createDebouncedWriter<ArtifactDraft>((d) => saveArtifactDraft(sessionId, itemKey, d), DRAFT_WRITE_MS),
  );

  const dirty = isArtifactDraftDirty(draft, originals);

  function setConflict(value: boolean) {
    conflictRef.current = value;
    setConflictState(value);
    if (!value) setConflictNote(null);
    syncLock();
  }

  /** The page is read-only while a save runs, a conflict waits for the person's choice, or the draft is blocked. */
  function syncLock() {
    link.send({ type: 'eddy-edit-lock', locked: conflictRef.current || savingRef.current || !!blockedRef.current || frameLockedRef.current });
  }

  /** The iframe's ref callback: a NEW element means a new page, so only now does the old channel go. */
  const attachFrame = useCallback(
    (el: HTMLIFrameElement | null) => {
      if (el && el !== frameRef.current) {
        link.reset();
        remountPendingRef.current = false;
      }
      frameRef.current = el;
    },
    [link],
  );

  /**
   * The framed page said hello again: it navigated itself. Remount through the snapshot path (the draft is
   * kept and re-applied at the next `ready`), at most `MAX_FRAME_REMOUNTS` times per load, then lock.
   */
  function onFrameNavigated() {
    if (remountPendingRef.current || frameLockedRef.current) return;
    if (planFrameNavigation(frameNavsRef.current) === 'remount') {
      frameNavsRef.current += 1;
      adoptSnapshot(snapshotRef.current, kind === 'deck' ? selectedRef.current : null, true);
      return;
    }
    frameLockedRef.current = true;
    setFrameLocked(true);
    setNotice(null);
    syncLock();
    if (shouldReportFrameUnstable(isArtifactDraftDirty(draftRef.current, originalsRef.current))) onFrameUnstable?.();
  }
  navigatedRef.current = onFrameNavigated;

  function selectSlide(index: number) {
    selectedRef.current = index;
    setSelected(index);
  }

  function commitDraft(next: ArtifactDraft) {
    draftRef.current = next;
    setDraft(next);
    if (isArtifactDraftDirty(next, originalsRef.current)) writer.schedule(next);
    else {
      writer.cancel();
      clearCanvasDraft(sessionId, itemKey);
    }
  }

  function post(message: Record<string, unknown>) {
    link.send(message);
  }

  /** The page announced its blocks. Only the FIRST announcement of a load is the original inventory. */
  function onReady(raw: unknown) {
    if (inventoryRef.current !== null) return; // the echo that follows our own restore
    const blocks = blocksOf(raw);
    inventoryRef.current = blocks;
    if (pendingGotoRef.current !== null) {
      post({ type: 'eddy-edit-goto', slide: pendingGotoRef.current });
      pendingGotoRef.current = null;
    }
    const pending = pendingReapplyRef.current;
    if (pending) {
      pendingReapplyRef.current = null;
      const result = reapplyEdits(pending.draft, blocks, pending.originals);
      commitDraft({ base: snapshotRef.current.updatedAt, edits: result.mapped });
      if (result.placed > 0) post({ type: 'eddy-edit-restore', edits: restorePayload({ base: '', edits: result.mapped }) });
      setNotice(reapplyMessage(result));
      return;
    }
    if (conflictRef.current) return; // the held draft waits for the person's choice
    const edits = restorePayload(draftRef.current);
    if (Object.keys(edits).length > 0) post({ type: 'eddy-edit-restore', edits });
  }

  function onChange(id: string, html: string) {
    // The page is locked in these states and posts nothing; a message that still arrives is dropped.
    if (savingRef.current || conflictRef.current || blockedRef.current || frameLockedRef.current) return;
    if (kind === 'deck' && originalsRef.current) {
      // The slide being typed in is the slide a save should come back to.
      const from = slideOfBlockId(id);
      const at = from === null ? null : rowIndexOfOriginal(rowsOf(draftRef.current, originalsRef.current), from);
      if (at !== null) selectSlide(at);
    }
    commitDraft(recordChange(draftRef.current, inventoryRef.current, id, html));
    setSaveError(null);
    setNotice(null);
    onStatusChange('idle');
  }

  function onPortMessage(raw: unknown) {
    const d = raw as { type?: unknown; blocks?: unknown; id?: unknown; html?: unknown } | null;
    if (!d || typeof d !== 'object') return;
    if (d.type === 'eddy-edit-ready') onReady(d.blocks);
    else if (d.type === 'eddy-edit-change' && typeof d.id === 'string' && typeof d.html === 'string') onChange(d.id, d.html);
  }
  portHandlerRef.current = onPortMessage;

  useEffect(() => {
    // The only window-level message trusted is the page's one hello (edit-frame-link.ts); it hands over
    // the private channel, and everything after that arrives on the channel, not here.
    const listener = (e: MessageEvent) => {
      if (link.handleWindowMessage(e, frameRef.current?.contentWindow)) syncLockRef.current();
    };
    window.addEventListener('message', listener);
    return () => {
      window.removeEventListener('message', listener);
      link.reset();
    };
  }, [link]);
  const syncLockRef = useRef(syncLock);
  syncLockRef.current = syncLock;
  useEffect(() => {
    syncLockRef.current();
  }, [blockedReason]);

  useEffect(() => {
    const onHide = () => writer.flush();
    window.addEventListener('pagehide', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      writer.flush();
    };
  }, [writer]);

  useEffect(() => {
    onDirtyChange(dirty);
    return () => onDirtyChange(false);
  }, [dirty, onDirtyChange]);

  /** A new load of the edit view: the next `ready` is a fresh inventory. */
  function adoptSnapshot(next: Snapshot, keepSlide: number | null = null, navigated = false) {
    snapshotRef.current = next;
    setSnapshot(next);
    inventoryRef.current = null;
    // The channel is NOT reset here: until React swaps the iframe the old page is still the frame, and
    // resetting now would let its late hello be taken for the new page's. `attachFrame` resets it when
    // the NEW element attaches.
    remountPendingRef.current = true;
    if (!navigated) {
      frameNavsRef.current = 0;
      frameLockedRef.current = false;
      setFrameLocked(false);
    }
    pendingGotoRef.current = keepSlide;
    setLoadId((n) => n + 1);
    selectSlide(keepSlide ?? 0);
  }

  async function fetchFresh(): Promise<ArtifactDetail> {
    const fresh = await api.get<ArtifactDetail>(artifactDetailPath(kind, slug));
    queryClient.setQueryData(detailKey, fresh);
    return fresh;
  }

  /** Reload the edit view at the server's current version. False when it could not be read. */
  async function reloadAtLatest(): Promise<boolean> {
    try {
      const fresh = await fetchFresh();
      adoptSnapshot({ updatedAt: fresh.updatedAt, body: fresh.body });
      return true;
    } catch {
      setSaveError("Couldn't load EDDY's version. Try again.");
      return false;
    }
  }

  // Eddy (or another tab) changed it: from a refetch of the detail, or an `artifact-updated` event.
  const announced = useFrameVersion(kind, slug);
  useEffect(() => {
    for (const observed of [detail.updatedAt, announced]) {
      const action = planServerVersion({ observed, snapshotAt: snapshot.updatedAt, saving, dirty });
      if (action === 'conflict') setConflict(true);
      else if (action === 'reload' && !reloadingRef.current) {
        reloadingRef.current = true;
        void reloadAtLatest().finally(() => {
          reloadingRef.current = false;
        });
      }
    }
    // reloadAtLatest only closes over stable refs/setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.updatedAt, announced, snapshot.updatedAt, saving, dirty]);

  function discard() {
    writer.cancel();
    clearCanvasDraft(sessionId, itemKey);
    pendingReapplyRef.current = null;
    commitDraft(emptyDraft(snapshotRef.current.updatedAt));
    setConflict(false);
    setNotice(null);
    setSaveError(null);
    // The page still shows the typed text; reloading puts the saved text back.
    adoptSnapshot(snapshotRef.current);
  }

  async function loadEddysVersion() {
    const ok = await reloadAtLatest();
    if (!ok) return;
    writer.cancel();
    clearCanvasDraft(sessionId, itemKey);
    pendingReapplyRef.current = null;
    commitDraft(emptyDraft(snapshotRef.current.updatedAt));
    setConflict(false);
    setNotice(null);
    setSaveError(null);
  }

  async function reapplyMine() {
    pendingReapplyRef.current = { draft: draftRef.current, originals: originalsRef.current };
    const ok = await reloadAtLatest();
    if (!ok) {
      pendingReapplyRef.current = null;
      return;
    }
    setConflict(false);
    setSaveError(null);
  }

  function save(): Promise<CanvasFlushResult> {
    if (inFlightRef.current) return inFlightRef.current;
    const run = runSave().finally(() => {
      inFlightRef.current = null;
    });
    inFlightRef.current = run;
    return run;
  }

  async function runSave(): Promise<CanvasFlushResult> {
    const sent = draftRef.current;
    const sentOriginals = originalsRef.current;
    if (!isArtifactDraftDirty(sent, sentOriginals)) return 'clean';
    savingRef.current = true;
    setSaving(true);
    setSaveError(null);
    onStatusChange('saving');
    frameRef.current?.blur();
    syncLock(); // read-only BEFORE the request leaves, so a keystroke cannot land after the body was built
    try {
      const body = buildSaveBody(sent, sentOriginals, snapshotRef.current.updatedAt);
      const res = await api.put<{ updatedAt: string }>(`${artifactDetailPath(kind, slug)}/manual-edit`, body);
      let next: Snapshot;
      try {
        const fresh = await fetchFresh();
        next = { updatedAt: fresh.updatedAt, body: fresh.body };
      } catch {
        void queryClient.invalidateQueries({ queryKey: detailKey });
        next = {
          updatedAt: res.updatedAt,
          body: sentOriginals ? JSON.stringify({ slides: slidesAfterSave(sent, sentOriginals) }) : snapshotRef.current.body,
        };
      }
      // Ids are positional and can shift: the draft restarts from the new version's inventory.
      writer.cancel();
      clearCanvasDraft(sessionId, itemKey);
      commitDraft(emptyDraft(next.updatedAt));
      setConflict(false);
      setNotice(null);
      // A deck comes back on the slide the person was on (clamped: the save may have deleted slides).
      const slides = sentOriginals ? (parseDeckBody(next.body)?.length ?? 0) : 0;
      adoptSnapshot(next, slides > 0 ? Math.min(selectedRef.current, slides - 1) : null);
      onStatusChange('saved');
      return 'saved';
    } catch (err) {
      const refusal = classifySaveRefusal(err);
      if (refusal?.kind === 'stale') {
        try {
          await fetchFresh();
        } catch {
          void queryClient.invalidateQueries({ queryKey: detailKey });
        }
        setConflict(true);
        onStatusChange('idle');
        return 'conflict';
      }
      if (refusal?.kind === 'unmatched') {
        // Nothing was written. Same way out as a conflict: "Re-apply my edits" reloads this version and
        // places each edit by its original text.
        setConflict(true);
        setConflictNote(refusal.message);
        onStatusChange('idle');
        return 'conflict';
      }
      // A blocked deck means this panel's read still says "editable": refetch it so the panel re-derives its state.
      if (refusal?.kind === 'message' && refusal.deckBlocked) void queryClient.invalidateQueries({ queryKey: detailKey });
      setSaveError(refusal?.kind === 'message' ? refusal.message : "Couldn't save. Your changes are kept, try again.");
      onStatusChange('idle');
      return 'failed';
    } finally {
      savingRef.current = false;
      setSaving(false);
      syncLock();
    }
  }

  /** The save-before-send flush (canvas-send-gate.ts): the same save the Save button runs. */
  async function flushDirtySave(): Promise<CanvasFlushResult> {
    if (inFlightRef.current) {
      await inFlightRef.current;
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
      return (await flushRef?.current?.()) ?? 'clean';
    }
    if (!isArtifactDraftDirty(draftRef.current, originalsRef.current)) return 'clean';
    if (conflictRef.current || blockedRef.current) return 'conflict';
    return save();
  }
  useEffect(() => {
    if (!flushRef) return;
    flushRef.current = flushDirtySave;
    const mine = flushDirtySave;
    return () => {
      if (flushRef.current === mine) flushRef.current = null;
    };
  });

  function onStripAction(action: StripAction, index: number) {
    if (!originals || saving || blockedReason) return;
    const d = draftRef.current;
    if (action === 'moveUp') {
      commitDraft(moveSlide(d, originals, index, -1));
      selectSlide(Math.max(0, index - 1));
    } else if (action === 'moveDown') {
      commitDraft(moveSlide(d, originals, index, 1));
      selectSlide(index + 1);
    } else if (action === 'duplicate') {
      commitDraft(duplicateSlide(d, originals, index));
      selectSlide(index + 1);
    } else {
      commitDraft(deleteSlide(d, originals, index));
      selectSlide(Math.max(0, Math.min(index, rowsOf(d, originals).length - 2)));
    }
    setNotice(null);
    onStatusChange('idle');
  }

  function onSelectSlide(index: number) {
    selectSlide(index);
    const row = originals ? rowsOf(draftRef.current, originals)[index] : undefined;
    if (row) post({ type: 'eddy-edit-goto', slide: row.from });
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {conflict && (
        <div
          role="alert"
          data-testid="canvas-conflict"
          className="shrink-0 border-b border-border bg-muted px-4 py-3 text-sm text-foreground"
        >
          <p className="mb-2">{conflictNote ?? `EDDY changed this ${kind} after you started editing.`}</p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="outline" disabled={saving} onClick={() => void reapplyMine()}>
              Re-apply my edits
            </Button>
            <Button type="button" size="sm" variant="outline" disabled={saving} onClick={() => void loadEddysVersion()}>
              Load EDDY's version
            </Button>
          </div>
        </div>
      )}
      {notice && !conflict && (
        <p
          role="status"
          data-testid="canvas-reapply-result"
          className="shrink-0 border-b border-border bg-muted px-4 py-2 text-xs text-muted-foreground"
        >
          {notice}
        </p>
      )}
      {originals && kind === 'deck' && arrangeOpen && (
        <CanvasSlideStrip
          rows={rowsOf(draft, originals)}
          originals={originals}
          selected={selected}
          onSelect={onSelectSlide}
          onAction={onStripAction}
          onNotes={(index, notes) => {
            if (savingRef.current || blockedRef.current) return;
            commitDraft(setSlideNotes(draftRef.current, originals, index, notes));
            onStatusChange('idle');
          }}
          maxSlides={detail.maxSlides}
          frozenReason={saving ? SAVING_REASON : blockedReason}
        />
      )}
      {frameLocked && (
        <p role="alert" data-testid="canvas-frame-unstable" className="shrink-0 border-b border-border bg-muted px-4 py-2 text-xs text-foreground">
          {frameKeepsReloadingReason(kind)}
        </p>
      )}
      <div className="relative flex min-h-0 flex-1 flex-col" inert={frameLocked}>
        <iframe
          key={`${snapshot.updatedAt}:${loadId}`}
          ref={attachFrame}
          data-testid="canvas-edit-frame"
          src={editFrameSrc(slug, snapshot.updatedAt)}
          title={`${title} (editing)`}
          sandbox={canvasFrameSandbox(kind)}
          // FRONT-2744: a deck's own F key may go fullscreen here too, as the chat card's hint promises.
          allowFullScreen={canvasFrameAllowsFullscreen(kind)}
          className="w-full flex-1 border-0 bg-canvas-surface"
        />
        {(saving || frameLocked) && <div data-testid="canvas-edit-shield" aria-hidden className="absolute inset-0" />}
      </div>
      {(saveError || (dirty && (conflict || blockedReason))) && (
        <p
          data-testid="canvas-save-reason"
          role={saveError && !blockedReason ? 'alert' : undefined}
          className={`shrink-0 px-4 pb-2 pt-2 text-xs ${saveError && !blockedReason ? 'text-destructive' : 'text-muted-foreground'}`}
        >
          {blockedReason ?? saveError ?? ARTIFACT_CONFLICT_SAVE_REASON}
        </p>
      )}
      <ActionBar
        dirty={dirty}
        saving={saving}
        saveDisabled={conflict || !!blockedReason}
        message="Unsaved changes"
        onSave={() => void save()}
        onDiscard={discard}
      />
    </div>
  );
}
